import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import Footer from '../components/Footer';
import { logError } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Plain account creation — no plan selection up front.
 * Customers can browse, sign up, and choose a plan later from their dashboard.
 */
const CreateAccount = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = 'First name is required';
    if (!formData.lastName.trim()) e.lastName = 'Last name is required';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Email is invalid';
    if (!formData.phoneNumber.trim()) e.phoneNumber = 'Phone number is required';
    if (!formData.password) e.password = 'Password is required';
    else if (formData.password.length < 8) e.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!formData.agreeToTerms) e.agreeToTerms = 'You must agree to the terms';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phoneNumber,
          password: formData.password,
          is_subscription: false,
          plan_type: 'lets-be-pals',
        }),
      });
      if (!res.ok) throw new Error('Account creation failed');

      // Auto-login by storing customer info, mirroring login flow
      sessionStorage.setItem('customerInfo', JSON.stringify({
        email: formData.email,
        name: `${formData.firstName} ${formData.lastName}`,
        isSubscription: false,
        subscription_status: 'inactive',
      }));
      sessionStorage.setItem('isLoggedIn', 'true');
      navigate('/dashboard');
    } catch (err) {
      logError('Account creation error:', err);
      setErrors({ submit: 'Could not create your account. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-page" data-testid="create-account-page">
      <nav className="nav-header">
        <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img
            src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png"
            alt="ReturnPal"
            className="logo-image"
            style={{ height: '50px', width: 'auto' }}
          />
        </div>
      </nav>

      <div className="signup-container">
        <div className="signup-content" style={{ maxWidth: 520 }}>
          <h1 className="signup-title">Create Your Account</h1>
          <p className="signup-subtitle" style={{ marginBottom: '2rem' }}>
            Sign up first — pick a plan later when you're ready to book a pickup.
          </p>

          <form onSubmit={handleSubmit} className="signup-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={errors.firstName ? 'input-error' : ''}
                  data-testid="ca-first-name"
                />
                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
              </div>
              <div className="form-group">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={errors.lastName ? 'input-error' : ''}
                  data-testid="ca-last-name"
                />
                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                className={errors.phoneNumber ? 'input-error' : ''}
                data-testid="ca-phone"
              />
              {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
            </div>

            <div className="form-group">
              <Label htmlFor="email">Email (Your Username)</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'input-error' : ''}
                data-testid="ca-email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <Label htmlFor="password">Create Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Minimum 8 characters"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'input-error' : ''}
                data-testid="ca-password"
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-group">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? 'input-error' : ''}
                data-testid="ca-confirm-password"
              />
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>

            <div className="form-group-checkbox">
              <div className="checkbox-wrapper">
                <Checkbox
                  id="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onCheckedChange={(checked) => {
                    setFormData((p) => ({ ...p, agreeToTerms: checked }));
                    if (errors.agreeToTerms) setErrors((p) => ({ ...p, agreeToTerms: '' }));
                  }}
                  data-testid="ca-agree-terms"
                />
                <Label htmlFor="agreeToTerms" className="checkbox-label">
                  I agree to the{' '}
                  <span onClick={() => window.open('/terms', '_blank')} style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>
                    Terms of Service
                  </span>
                  {' and '}
                  <span onClick={() => window.open('/privacy', '_blank')} style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>
                    Privacy Policy
                  </span>
                </Label>
              </div>
              {errors.agreeToTerms && <span className="error-message">{errors.agreeToTerms}</span>}
            </div>

            {errors.submit && (
              <div className="error-message" style={{ marginBottom: '1rem', color: '#c00' }} data-testid="ca-submit-error">
                {errors.submit}
              </div>
            )}

            <Button
              type="submit"
              className="btn-primary btn-large w-full"
              disabled={isSubmitting}
              data-testid="ca-submit-btn"
            >
              {isSubmitting ? 'Creating account…' : 'Create Account'}
            </Button>

            <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
              Already have an account?{' '}
              <span
                onClick={() => navigate('/login')}
                style={{ color: '#10b981', cursor: 'pointer', fontWeight: 600 }}
              >
                Log in
              </span>
            </p>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateAccount;
